// ============================================================================
// FIRESTORE PATHS — Canonical path builder module
//
// Single source of truth for every Firestore collection/document path.
// No code outside this module should contain inline Firestore path strings.
//
// CANONICAL TERRAIN:
//   /users/{uid}
//     /learning/state              (per-user learning progress — no PHI)
//   /tenants/{tenantId}
//     /members/{uid}
//     /specialties/{specialtyId}
//       /packs/{packDocId}
//     /referrals/{referralId}
//       /auditLogs/{runId}
//       /workflow/{docId}         (workflow states)
//       /workflowEvents/{eventId} (append-only event log)
//       /phi/patient_identity     (PHI — Step 2+; locked in Step 1)
//       /phi/contact              (PHI — Step 2+; locked in Step 1)
//       /phi/insurance            (PHI — Step 2+; locked in Step 1)
//       /phi/attachments/{id}     (PHI — Step 2+; locked in Step 1)
//     /phi_access_logs/{logId}   (PHI audit log — Step 2+; locked in Step 1)
//     /audit_logs/{runId}         (flat copy for dashboards)
//     /metrics/{eventId}
//     /settings/{settingId}
//     /submissions/{submissionKey}
//     /runSessions/{runId}
//     /rollups/daily/dates/{dateId}
//     /ops/{errorId}
//     /adminEvents/{eventId}        (tenant lifecycle audit trail)
//   /platform/rollups/daily/{dateId}
//   /platform_metrics/**
//   /seed_templates/**
// ============================================================================

// ============================================================================
// DOCUMENT PATH BUILDERS
// Each function returns a string consumable by Firestore v9 doc(db, path).
// ============================================================================

/** /users/{uid} */
export const user = (uid) => `users/${uid}`;

/** /users/{uid}/learning/state */
export const userLearningState = (uid) => `users/${uid}/learning/state`;

/** /tenants/{tenantId} */
export const tenant = (tenantId) => `tenants/${tenantId}`;

/** /tenants/{tenantId}/members/{uid} */
export const tenantMember = (tenantId, uid) => `tenants/${tenantId}/members/${uid}`;

/** /tenants/{tenantId}/specialties/{specialtyId} */
export const specialty = (tenantId, specialtyId) =>
  `tenants/${tenantId}/specialties/${specialtyId}`;

/** /tenants/{tenantId}/specialties/{specialtyId}/packs/{packDocId} */
export const pack = (tenantId, specialtyId, packDocId) =>
  `tenants/${tenantId}/specialties/${specialtyId}/packs/${packDocId}`;

/** /tenants/{tenantId}/referrals/{referralId} */
export const referral = (tenantId, referralId) =>
  `tenants/${tenantId}/referrals/${referralId}`;

/** /tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId} */
export const referralAuditLog = (tenantId, referralId, runId) =>
  `tenants/${tenantId}/referrals/${referralId}/auditLogs/${runId}`;

/** /tenants/{tenantId}/referrals/{referralId}/workflow/{docId} */
export const referralWorkflow = (tenantId, referralId, docId) =>
  `tenants/${tenantId}/referrals/${referralId}/workflow/${docId}`;

/** /tenants/{tenantId}/audit_logs/{runId}  (flat copy for dashboard queries) */
export const auditLogFlat = (tenantId, runId) =>
  `tenants/${tenantId}/audit_logs/${runId}`;

/** /tenants/{tenantId}/metrics/{eventId} */
export const metricEvent = (tenantId, eventId) =>
  `tenants/${tenantId}/metrics/${eventId}`;

/** /tenants/{tenantId}/settings/{settingId} */
export const setting = (tenantId, settingId) =>
  `tenants/${tenantId}/settings/${settingId}`;

/** /tenants/{tenantId}/settings/impact  (well-known doc) */
export const impactSettings = (tenantId) =>
  `tenants/${tenantId}/settings/impact`;

/**
 * /tenants/{tenantId}/settings/schedulingAvailability  (well-known doc)
 * Source of truth for appointment BOOKING constraints (allowed days/times).
 * Separate from settings/impact.businessHours, which governs SLA timing only.
 */
export const schedulingAvailability = (tenantId) =>
  `tenants/${tenantId}/settings/schedulingAvailability`;

/** /tenants/{tenantId}/settings/phiPolicy  (well-known doc) */
export const phiPolicy = (tenantId) =>
  `tenants/${tenantId}/settings/phiPolicy`;

/**
 * /tenants/{tenantId}/integrations/ehr/connections/{connectionId}  (Build 2A)
 * Never-PHI / never-secret tenant EHR (SMART) integration config. Writes are
 * Cloud-Function-only; reads gated to platform_admin / tenant_admin in rules.
 * (Even-segment document path — the alternating ehr/connections subdoc form.)
 */
export const integrationEhrConnection = (tenantId, connectionId) =>
  `tenants/${tenantId}/integrations/ehr/connections/${connectionId}`;

/**
 * /tenants/{tenantId}/ehrLaunchSessions/{launchSessionId}  (Build 2B)
 * Server-only SMART launch session (opaque launch context + flow secrets;
 * never a token/auth-code/PHI). Firestore rules deny all client access.
 */
export const ehrLaunchSession = (tenantId, launchSessionId) =>
  `tenants/${tenantId}/ehrLaunchSessions/${launchSessionId}`;

/** /tenants/{tenantId}/submissions/{submissionKey} */
export const submission = (tenantId, submissionKey) =>
  `tenants/${tenantId}/submissions/${submissionKey}`;

/** /tenants/{tenantId}/runSessions/{runId} */
export const runSession = (tenantId, runId) =>
  `tenants/${tenantId}/runSessions/${runId}`;

/** /tenants/{tenantId}/rollups/daily/dates/{dateId} */
export const rollupDaily = (tenantId, dateId) =>
  `tenants/${tenantId}/rollups/daily/dates/${dateId}`;

/** /tenants/{tenantId}/rollups/ops/activeWorkflows/__snapshot
 *  Ops metrics snapshot: rolling 30-day speed medians + stuck item counts.
 *  __snapshot is a well-known doc within the activeWorkflows subcollection.
 *  Per-referral mirror docs live at activeWorkflows/{referralId} (server-internal).
 *  Written by: recomputeAgingBucketsDaily (Cloud Function, daily at 06:00 UTC).
 *  Read by: OpsHomeView (speed + stuck sections).
 */
export const rollupOpsSnapshot = (tenantId) =>
  `tenants/${tenantId}/rollups/ops/activeWorkflows/__snapshot`;

/** /tenants/{tenantId}/ops/{errorId}  (4-segment valid doc path; matches writePhiOpsError) */
export const opsError = (tenantId, errorId) =>
  `tenants/${tenantId}/ops/${errorId}`;

/** /tenants/{tenantId}/rerunLocks/{referralId} */
export const rerunLock = (tenantId, referralId) =>
  `tenants/${tenantId}/rerunLocks/${referralId}`;

/** /tenants/{tenantId}/phiDismissals/{dismissalId} */
export const phiDismissal = (tenantId, dismissalId) =>
  `tenants/${tenantId}/phiDismissals/${dismissalId}`;

/** /platform/ops/errors/{errorId} — platform-level ops index */
export const platformOpsError = (errorId) =>
  `platform/ops/errors/${errorId}`;

/** /platform_metrics/days/items/{dateId} — Cloud Function rollup docs */
export const platformMetricsDay = (dateId) =>
  `platform_metrics/days/items/${dateId}`;

/** /seed_templates/data/specialties/{specialtyId} */
export const seedSpecialtyDoc = (specialtyId) =>
  `seed_templates/data/specialties/${specialtyId}`;

/** /seed_templates/data/packs/{packId} */
export const seedPackDoc = (packId) =>
  `seed_templates/data/packs/${packId}`;

/** /tenants/{tenantId}/join_requests/{uid} */
export const joinRequest = (tenantId, uid) =>
  `tenants/${tenantId}/join_requests/${uid}`;

/** /tenants/{tenantId}/referralDrafts/{draftId}  (routing draft — PHI-free) */
export const referralDraft = (tenantId, draftId) =>
  `tenants/${tenantId}/referralDrafts/${draftId}`;

/** /tenants/{tenantId}/adminEvents/{eventId}  (lifecycle + admin audit trail) */
export const tenantAdminEvent = (tenantId, eventId) =>
  `tenants/${tenantId}/adminEvents/${eventId}`;

/** /invites/{inviteId} */
export const invite = (inviteId) => `invites/${inviteId}`;

/** /tenants/{tenantId}/referrals/{referralId}/workflow/state  (singleton workflow state doc) */
export const workflowState = (tenantId, referralId) =>
  `tenants/${tenantId}/referrals/${referralId}/workflow/state`;

/** /tenants/{tenantId}/referrals/{referralId}/workflowEvents/{eventId}  (append-only event log) */
export const workflowEvent = (tenantId, referralId, eventId) =>
  `tenants/${tenantId}/referrals/${referralId}/workflowEvents/${eventId}`;

/** /tenants/{tenantId}/referrals/{referralId}/reworkRequests/{requestId}  (missing-items requests) */
export const reworkRequest = (tenantId, referralId, requestId) =>
  `tenants/${tenantId}/referrals/${referralId}/reworkRequests/${requestId}`;

/** /tenants/{tenantId}/referrals/{referralId}/reworkTouches/{touchId}  (outreach touch log) */
export const reworkTouch = (tenantId, referralId, touchId) =>
  `tenants/${tenantId}/referrals/${referralId}/reworkTouches/${touchId}`;

/**
 * /tenants/{tenantId}/referrals/{referralId}/appointmentInstances/{appointmentInstanceId}
 * Stable, non-PHI appointment instance ledger. One document per booked appointment
 * instance; a reschedule closes the old instance (RESCHEDULED_OUT) and creates a new one.
 */
export const appointmentInstance = (tenantId, referralId, appointmentInstanceId) =>
  `tenants/${tenantId}/referrals/${referralId}/appointmentInstances/${appointmentInstanceId}`;

/**
 * /tenants/{tenantId}/referrals/{referralId}/communications/{communicationId}
 * Referral Pings — internal, non-PHI attention signals bound to a referral.
 * threadId === referralId. Not chat, not document transport, no PHI.
 */
export const communication = (tenantId, referralId, communicationId) =>
  `tenants/${tenantId}/referrals/${referralId}/communications/${communicationId}`;

/**
 * /tenants/{tenantId}/referrals/{referralId}/communications/{communicationId}/messages/{messageId}
 * Referral Ping replies — append-only, non-PHI conversation trail under a ping.
 * Not chat, not document transport, no PHI. The parent ping owns status.
 */
export const communicationMessage = (tenantId, referralId, communicationId, messageId) =>
  `tenants/${tenantId}/referrals/${referralId}/communications/${communicationId}/messages/${messageId}`;

/**
 * /tenants/{tenantId}/queuePings/{queuePingId}
 * Queue Pings — tenant-level, sector-targeted operational attention signals
 * (one fire → one sector role → many receivers, acknowledge-only). Non-PHI,
 * not referral-bound. Distinct from referral communications.
 */
export const queuePing = (tenantId, queuePingId) =>
  `tenants/${tenantId}/queuePings/${queuePingId}`;

/**
 * /tenants/{tenantId}/queuePings/{queuePingId}/deliveries/{uid}
 * Per-recipient delivery + acknowledgment state. Doc id === recipient uid.
 */
export const queuePingDelivery = (tenantId, queuePingId, uid) =>
  `tenants/${tenantId}/queuePings/${queuePingId}/deliveries/${uid}`;

// ── Pre-Referral Packets (Phase 1: Referral Packet Inbox Foundation) ─────
// A Pre-Referral Packet is a one-patient, pre-referral source container made
// of one or more documents that may later be promoted into a referral on Gate
// submit. Packet ROOT docs are Never-PHI (no patient identifiers, no original
// filenames). Packet DOCUMENT manifests are PHI — server-only via Cloud
// Functions.
//
// PHI manifest path mirrors the existing referral attachment manifest
// convention `phi/attachments/docs/{attachmentId}` — a container doc inside
// the `phi` subcollection holds a `docs` subcollection of per-item manifests.
// This keeps PHI subtree-style deny rules simple and consistent.

/** /tenants/{tenantId}/preReferralPackets/{packetId}  (Never-PHI root) */
export const preReferralPacket = (tenantId, packetId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}`;

/**
 * /tenants/{tenantId}/preReferralPackets/{packetId}/phi/documents/docs/{documentId}
 * PHI manifest — server-only. The `documents` container doc holds the `docs`
 * subcollection, matching the existing `phi/attachments/docs/{attachmentId}`
 * convention. Required to produce an even-segment-count Firestore doc path.
 */
export const preReferralPacketDocument = (tenantId, packetId, documentId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}/phi/documents/docs/${documentId}`;

// ── Phase 2A: OCR extraction (PHI; server-only) ──────────────────────────────
// `extraction` is a well-known doc inside the `phi` collection (sibling to the
// `documents` doc), holding jobs / rawResults / candidates subcollections.
// All paths are PHI — client direct access stays denied by the /phi/** rule.

/** /tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/jobs/{jobId} */
export const preReferralPacketExtractionJob = (tenantId, packetId, jobId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/jobs/${jobId}`;

/** /tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/rawResults/{resultId} */
export const preReferralPacketExtractionRawResult = (tenantId, packetId, resultId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/rawResults/${resultId}`;

/** /tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/candidates/{candidateId} */
export const preReferralPacketExtractionCandidate = (tenantId, packetId, candidateId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/candidates/${candidateId}`;

// ── Phase 2C-3: clinical evidence candidates (PHI; server-only) ───────────────
// Pack-aware clinical evidence matched from packet OCR against the selected
// published Pack's evidence maps. Distinct from the patient/admin `extraction`
// candidates above. Under /phi/** → client-denied by existing Firestore rules.

/** /tenants/{tenantId}/preReferralPackets/{packetId}/phi/clinicalEvidence/candidates/{candidateId} */
export const preReferralPacketClinicalEvidenceCandidate = (tenantId, packetId, candidateId) =>
  `tenants/${tenantId}/preReferralPackets/${packetId}/phi/clinicalEvidence/candidates/${candidateId}`;

// ── PHI document paths (Step 1: reserved; no client reads/writes yet) ─────

/** /tenants/{tenantId}/referrals/{referralId}/phi/patient_identity */
export const phiPatientIdentity = (tenantId, referralId) =>
  `tenants/${tenantId}/referrals/${referralId}/phi/patient_identity`;

/** /tenants/{tenantId}/referrals/{referralId}/phi/contact */
export const phiContact = (tenantId, referralId) =>
  `tenants/${tenantId}/referrals/${referralId}/phi/contact`;

/** /tenants/{tenantId}/referrals/{referralId}/phi/insurance */
export const phiInsurance = (tenantId, referralId) =>
  `tenants/${tenantId}/referrals/${referralId}/phi/insurance`;

/** /tenants/{tenantId}/referrals/{referralId}/phi/attachments/docs/{attachmentId} */
export const phiAttachment = (tenantId, referralId, attachmentId) =>
  `tenants/${tenantId}/referrals/${referralId}/phi/attachments/docs/${attachmentId}`;

/** /tenants/{tenantId}/phi_access_logs/{logId} */
export const phiAccessLog = (tenantId, logId) =>
  `tenants/${tenantId}/phi_access_logs/${logId}`;

/**
 * /tenants/{tenantId}/securityEvents/{eventId}
 * Never-PHI structured security-event audit sink (denials + boundary violations).
 * Server-written only (Cloud Functions / Admin SDK); clients are denied by the
 * default catch-all rule. No patient values / tokens / raw URLs / filenames.
 */
export const securityEvent = (tenantId, eventId) =>
  `tenants/${tenantId}/securityEvents/${eventId}`;

/** /platform/rollups/daily/{dateId} */
export const platformRollupDaily = (dateId) =>
  `platform/rollups/daily/${dateId}`;

/** /platform_metrics/{...rest} — use for platform Cloud Function writes */
export const platformMetrics = (path) =>
  `platform_metrics/${path}`;

/** /seed_templates/{...rest} */
export const seedTemplate = (path) =>
  `seed_templates/${path}`;

// ============================================================================
// COLLECTION PATH BUILDERS
// Each function returns a string consumable by Firestore v9 collection(db, path).
// ============================================================================

export const col = {
  /** users */
  users: () => 'users',

  /** tenants */
  tenants: () => 'tenants',

  /** tenants/{tenantId}/members */
  members: (tenantId) => `tenants/${tenantId}/members`,

  /** tenants/{tenantId}/specialties */
  specialties: (tenantId) => `tenants/${tenantId}/specialties`,

  /** tenants/{tenantId}/specialties/{specialtyId}/packs */
  packs: (tenantId, specialtyId) =>
    `tenants/${tenantId}/specialties/${specialtyId}/packs`,

  /** tenants/{tenantId}/referrals */
  referrals: (tenantId) => `tenants/${tenantId}/referrals`,

  /** tenants/{tenantId}/referrals/{referralId}/auditLogs */
  referralAuditLogs: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/auditLogs`,

  /** tenants/{tenantId}/referrals/{referralId}/workflow */
  referralWorkflow: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/workflow`,

  /** tenants/{tenantId}/referrals/{referralId}/appointmentInstances */
  appointmentInstances: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/appointmentInstances`,

  /** tenants/{tenantId}/referrals/{referralId}/communications */
  communications: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/communications`,

  /** tenants/{tenantId}/referrals/{referralId}/communications/{communicationId}/messages */
  communicationMessages: (tenantId, referralId, communicationId) =>
    `tenants/${tenantId}/referrals/${referralId}/communications/${communicationId}/messages`,

  /** tenants/{tenantId}/queuePings */
  queuePings: (tenantId) => `tenants/${tenantId}/queuePings`,

  /** tenants/{tenantId}/queuePings/{queuePingId}/deliveries */
  queuePingDeliveries: (tenantId, queuePingId) =>
    `tenants/${tenantId}/queuePings/${queuePingId}/deliveries`,

  /** tenants/{tenantId}/audit_logs  (flat collection) */
  auditLogsFlat: (tenantId) => `tenants/${tenantId}/audit_logs`,

  /** tenants/{tenantId}/metrics */
  metrics: (tenantId) => `tenants/${tenantId}/metrics`,

  /** tenants/{tenantId}/settings */
  settings: (tenantId) => `tenants/${tenantId}/settings`,

  /** tenants/{tenantId}/integrations/ehr/connections  (Build 2A — never-PHI EHR config) */
  integrationEhrConnections: (tenantId) => `tenants/${tenantId}/integrations/ehr/connections`,

  /** tenants/{tenantId}/ehrLaunchSessions  (Build 2B — server-only launch sessions) */
  ehrLaunchSessions: (tenantId) => `tenants/${tenantId}/ehrLaunchSessions`,

  /** tenants/{tenantId}/submissions */
  submissions: (tenantId) => `tenants/${tenantId}/submissions`,

  /** tenants/{tenantId}/runSessions */
  runSessions: (tenantId) => `tenants/${tenantId}/runSessions`,

  /** tenants/{tenantId}/rollups/daily/dates */
  rollupDates: (tenantId) => `tenants/${tenantId}/rollups/daily/dates`,

  /** tenants/{tenantId}/ops  (collection of ops error docs; matches writePhiOpsError) */
  opsErrors: (tenantId) => `tenants/${tenantId}/ops`,

  /** tenants/{tenantId}/rerunLocks */
  rerunLocks: (tenantId) => `tenants/${tenantId}/rerunLocks`,

  /** tenants/{tenantId}/phiDismissals */
  phiDismissals: (tenantId) => `tenants/${tenantId}/phiDismissals`,

  /** platform/ops/errors — platform-level ops index */
  platformOpsErrors: () => 'platform/ops/errors',

  /** platform_metrics/tenant_days/items */
  platformMetricsTenantDays: () => 'platform_metrics/tenant_days/items',

  /** seed_templates/data/specialties */
  seedSpecialties: () => 'seed_templates/data/specialties',

  /** seed_templates/data/packs */
  seedPacks: () => 'seed_templates/data/packs',

  /** tenants/{tenantId}/join_requests */
  joinRequests: (tenantId) => `tenants/${tenantId}/join_requests`,

  /** tenants/{tenantId}/referralDrafts */
  referralDrafts: (tenantId) => `tenants/${tenantId}/referralDrafts`,

  /** invites (root collection) */
  invites: () => 'invites',

  /** tenants/{tenantId}/referrals/{referralId}/workflowEvents */
  workflowEvents: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/workflowEvents`,

  /** tenants/{tenantId}/referrals/{referralId}/reworkRequests */
  reworkRequests: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/reworkRequests`,

  /** tenants/{tenantId}/referrals/{referralId}/reworkTouches */
  reworkTouches: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/reworkTouches`,

  /** tenants/{tenantId}/referrals/{referralId}/phi/attachments/docs */
  phiAttachments: (tenantId, referralId) =>
    `tenants/${tenantId}/referrals/${referralId}/phi/attachments/docs`,

  /** tenants/{tenantId}/phi_access_logs */
  phiAccessLogs: (tenantId) => `tenants/${tenantId}/phi_access_logs`,

  /** tenants/{tenantId}/securityEvents  (Never-PHI security audit sink; server-written) */
  securityEvents: (tenantId) => `tenants/${tenantId}/securityEvents`,

  /** tenants/{tenantId}/adminEvents */
  tenantAdminEvents: (tenantId) => `tenants/${tenantId}/adminEvents`,

  /** tenants/{tenantId}/preReferralPackets  (Never-PHI packet index) */
  preReferralPackets: (tenantId) => `tenants/${tenantId}/preReferralPackets`,

  /** tenants/{tenantId}/preReferralPackets/{packetId}/phi/documents/docs  (PHI; server-only) */
  preReferralPacketDocuments: (tenantId, packetId) =>
    `tenants/${tenantId}/preReferralPackets/${packetId}/phi/documents/docs`,

  /** tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/jobs  (PHI; server-only) */
  preReferralPacketExtractionJobs: (tenantId, packetId) =>
    `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/jobs`,

  /** tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/rawResults  (PHI; server-only) */
  preReferralPacketExtractionRawResults: (tenantId, packetId) =>
    `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/rawResults`,

  /** tenants/{tenantId}/preReferralPackets/{packetId}/phi/extraction/candidates  (PHI; server-only) */
  preReferralPacketExtractionCandidates: (tenantId, packetId) =>
    `tenants/${tenantId}/preReferralPackets/${packetId}/phi/extraction/candidates`,

  /** tenants/{tenantId}/preReferralPackets/{packetId}/phi/clinicalEvidence/candidates  (PHI; server-only) */
  preReferralPacketClinicalEvidenceCandidates: (tenantId, packetId) =>
    `tenants/${tenantId}/preReferralPackets/${packetId}/phi/clinicalEvidence/candidates`,
};

// ============================================================================
// CONVENIENCE: PATHS object (spec-compatible named export)
// Bundles all doc builders + col into a single object for import convenience.
//
// Usage:
//   import { PATHS } from './firestorePaths.js';
//   doc(db, PATHS.user(uid))
//   collection(db, PATHS.col.referrals(tenantId))
// ============================================================================

export const PATHS = {
  user,
  userLearningState,
  tenant,
  tenantMember,
  specialty,
  pack,
  referral,
  referralAuditLog,
  referralWorkflow,
  auditLogFlat,
  metricEvent,
  setting,
  impactSettings,
  schedulingAvailability,
  phiPolicy,
  submission,
  runSession,
  rollupDaily,
  rollupOpsSnapshot,
  opsError,
  rerunLock,
  phiDismissal,
  platformRollupDaily,
  platformOpsError,
  platformMetrics,
  platformMetricsDay,
  seedTemplate,
  seedSpecialtyDoc,
  seedPackDoc,
  joinRequest,
  referralDraft,
  invite,
  workflowState,
  workflowEvent,
  reworkRequest,
  reworkTouch,
  appointmentInstance,
  communication,
  communicationMessage,
  queuePing,
  queuePingDelivery,
  phiPatientIdentity,
  phiContact,
  phiInsurance,
  phiAttachment,
  phiAccessLog,
  securityEvent,
  tenantAdminEvent,
  preReferralPacket,
  preReferralPacketDocument,
  preReferralPacketExtractionJob,
  preReferralPacketExtractionRawResult,
  preReferralPacketExtractionCandidate,
  preReferralPacketClinicalEvidenceCandidate,
  col,
};

export default PATHS;
